import React, { useContext, useState } from 'react';
import { useTranslations } from '@vp/om-translations';
import { Button, Link, Span, VisuallyHidden } from '@vp/swan';
import PropTypes from 'prop-types';
import { getShopperId } from '../../../util/auth';
import { OrderDetailsContext } from '../../contexts/OrderDetailsContext';
import OrderHistoryContext from '../../contexts/OrderHistoryContext';
import {
  docRemediationModalPresentedToCustomer,
  trackBuyItAgainEditDesignAndReorderClicked,
  trackLegacyEditDocumentUrlConversionSuccess,
} from '../../../apis/trackingApi';
import {
  trackDocumentConversionRemediationPresentedToCustomer,
  trackDocumentEditClicked,
} from '../../../apis/newRelicTrackingApi';
import {
  BUY_IT_AGAIN_SOURCE_PAGE,
  EDIT_ACTION,
} from '../../../constants/orderdetailsui';
import { SourcePageContext } from '../../contexts/SourcePageContext';
import { useLineItemData } from '../../contexts/LineItemDataProvider';
import { BasicPreloader } from '../BasicPreloader';
import { convertLegacyDocument } from '../../../apis/oreosApi';
import { LineItemReorderActionStatusContext } from '../../contexts/LineItemReorderActionStatusContext';
import { DOC_CONVERSION_FAILURE } from '../../../util/reorderResponseHelpers';

export const ItemEditDesignAndReorderCta = ({
  sourcePageOverride,
  ...buttonProps
}) => {
  const [orderDetailsMessages] = useTranslations(['orderDetailsMessages']);
  const orderHistoryContext = useContext(OrderHistoryContext);
  const orderDetailsContext = useContext(OrderDetailsContext);
  const item = useLineItemData();
  const {
    id,
    orderNumber,
    states: itemStates,
    editDocumentUrl,
    productName,
  } = item;
  const shopperId = getShopperId();
  const {
    setDocConversionFailure,
    setGenericFailure,
    setAltDocId,
    resetReorderActionFeedback,
  } = useContext(LineItemReorderActionStatusContext);

  const { sourcePage: sourcePageFromContext } = useContext(SourcePageContext);
  // conditionally override sourcePage for NR tracking if parameter provided
  const sourcePage = sourcePageOverride || sourcePageFromContext;

  // since this component is shared, it can appear in OH or OD
  // get the proper context to fetch values
  const contextToUse = orderDetailsContext || orderHistoryContext;
  const { tenantEnvironment, vistacartTenantName: tenant } = contextToUse;

  const [awaitingApiResponse, setAwaitingApiResponse] = useState(false);

  const { skin } = buttonProps;
  const applyLinkSkinToButton = skin === 'link';

  // call OREOs and handle an error in the case of a DOC_CONVERSION_FAILURE (we couldn't map the legacy item to an item on the new platform)
  const fetchEditDocUrlFromOreos = async () => {
    setAwaitingApiResponse(true);
    try {
      const data = await convertLegacyDocument({
        tenantEnvironment,
        orderNumber,
        lineItemId: id,
        tenant,
        sourcePage,
      });
      setAwaitingApiResponse(false);
      trackLegacyEditDocumentUrlConversionSuccess(item, sourcePage);
      // track edit doc url successfully retrieved from docGateway
      if (data.successful) {
        return data.docGatewayDesignDetails?.editUrl;
      }
      if (data.failureReason === DOC_CONVERSION_FAILURE) {
        // there was a doc conversion failure, lets pop that modal!
        setDocConversionFailure(true);
        setAltDocId(data.altDocId);
        // more tracking
        const analyticsMetadata = {
          sourcePage,
          shopperId,
          orderNumber,
          tenant,
          item,
          action: EDIT_ACTION,
        };
        docRemediationModalPresentedToCustomer(analyticsMetadata);
        trackDocumentConversionRemediationPresentedToCustomer(
          shopperId,
          analyticsMetadata
        );
      } else {
        // handle generic error
        setGenericFailure(true);
      }
    } catch (err) {
      // handle error
      setAwaitingApiResponse(false);
      setGenericFailure(true);
    }
  };

  // This function is called when a user clicks on the "Edit design and reorder" button
  const editDesignAndReorderOnClickHandler = async () => {
    resetReorderActionFeedback(EDIT_ACTION);

    // tracking data for metrics
    const trackingData = {
      orderNumber,
      itemStates,
      vistacartTenantName: tenant,
      page: sourcePage,
      editDocumentUrl: editDocumentUrl || 'Legacy Item',
    };
    trackDocumentEditClicked(shopperId, trackingData);
    // This was requested for the "Buy It Again" Experiment only
    if (sourcePageOverride === BUY_IT_AGAIN_SOURCE_PAGE) {
      trackBuyItAgainEditDesignAndReorderClicked();
    }
    // if editDocUrl is undefined we call OREOs to get the editDocUrl for a legacy item
    if (!editDocumentUrl || editDocumentUrl === '') {
      /* make call to OREOs to get editDocUrl. If there is an error (i.e. a DOC_CONVERSION_FAILURE) handle that and present modal */
      const editUrl = await fetchEditDocUrlFromOreos();

      if (editUrl) {
        // navigate to edit Document Url for legacy item returned from call to OREOs
        window.location.href = editUrl;
      }
    } else {
      window.location.href = editDocumentUrl;
    }
  };

  if (applyLinkSkinToButton) {
    return (
      <Link
        render={p => (
          <Button
            onClick={editDesignAndReorderOnClickHandler}
            disabled={awaitingApiResponse}
            fontWeight="bold"
            className={p.className}
            skin="link"
          >
            {p.children}
          </Button>
        )}
      >
        {awaitingApiResponse && <BasicPreloader size="tiny" />}
        {orderDetailsMessages.editButtonText}
        <VisuallyHidden>{`: ${productName}`}</VisuallyHidden>
      </Link>
    );
  }

  return (
    <Button
      onClick={editDesignAndReorderOnClickHandler}
      disabled={awaitingApiResponse}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...buttonProps}
    >
      {awaitingApiResponse && <BasicPreloader size="tiny" />}
      {/* This padding prevents the button from resizing when the preloader appears/disappears */}
      <Span mx={awaitingApiResponse ? 0 : 3}>
        {orderDetailsMessages.editDesignAndReorderButtonText}
      </Span>
      <VisuallyHidden>{`: ${productName}`}</VisuallyHidden>
    </Button>
  );
};

ItemEditDesignAndReorderCta.propTypes = {
  sourcePageOverride: PropTypes.string,
  buttonProps: PropTypes.any,
};
